import React from 'react';
import {
  Inbox,
  FileText,
  PlayCircle,
  Truck,
  Plane,
  Ship,
  Anchor,
  Boxes,
  Waves,
  PackageOpen,
  ShieldCheck,
  MapPin,
  CheckCircle2,
  Flag,
  Paperclip,
  Pencil,
  Trash2,
  Download,
  CalendarClock,
  Receipt,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Mapeo de nombre de icono (string) a componente Lucide.
 * Mantener sincronizado con el backend (que guarda el nombre
 * del icono como string, no la referencia, para no acoplar).
 */
const ICON_MAP = {
  Inbox,
  FileText,
  PlayCircle,
  Truck,
  // Aéreo
  Plane,
  // Marítimo (hitos LCL): consolidación, zarpe, tránsito, arribo,
  // desconsolidación y aduana. Solo se agregan claves nuevas — los
  // iconos del aéreo resuelven exactamente igual que antes.
  Ship,
  Anchor,
  Boxes,
  Waves,
  PackageOpen,
  ShieldCheck,
  MapPin,
  CheckCircle2,
  Flag,
  // Recargos que se publican desde el seguimiento de la operación.
  Receipt,
};

function pickIcon(name) {
  return ICON_MAP[name] || Flag;
}

/**
 * Opciones del selector de icono, con su etiqueta en español.
 *
 * Se exporta desde acá a propósito: es la MISMA lista que `ICON_MAP`,
 * así que no se puede ofrecer al operador un icono que después el
 * timeline no sepa dibujar (antes las dos listas vivían separadas y
 * la del selector tenía iconos que caían al genérico).
 */
export const ICON_OPTIONS = [
  { value: 'Flag', label: 'Genérico' },
  { value: 'Inbox', label: 'Recepción' },
  { value: 'FileText', label: 'Documento' },
  { value: 'PlayCircle', label: 'Inicio' },
  { value: 'Truck', label: 'Transporte terrestre' },
  { value: 'Plane', label: 'Vuelo' },
  { value: 'Ship', label: 'Zarpe / buque' },
  { value: 'Anchor', label: 'Puerto' },
  { value: 'Boxes', label: 'Consolidación' },
  { value: 'Waves', label: 'En tránsito' },
  { value: 'PackageOpen', label: 'Desconsolidación' },
  { value: 'ShieldCheck', label: 'Aduana' },
  { value: 'MapPin', label: 'Ubicación / entrega' },
  { value: 'CheckCircle2', label: 'Completado' },
  { value: 'Receipt', label: 'Recargo' },
];

/** Componente Lucide de un icono por su nombre. Cae en `Flag`. */
export const iconByName = pickIcon;

export { ICON_MAP };

function formatDateTime(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Línea de tiempo de hitos de una trazabilidad. Es el mismo
 * componente para la vista del operador y la página pública del
 * cliente: solo cambia la prop `mode`.
 *
 * Un hito es un evento que YA ocurrió — no tiene estado. Las únicas
 * acciones del operador son adjuntar, editar y eliminar.
 *
 * Props:
 *  - hitos: array de hitos (ordenados por `orden`).
 *  - mode: 'operator' | 'public'. Define si se muestran las acciones.
 *  - onEditStage?(stage): abre el modal de edición del hito.
 *  - onDeleteStage?(stage): elimina el hito.
 *  - onAttachFile?(stage): abre el file picker para adjuntar.
 *  - onDetachFile?(stage, fileKey): quita un archivo.
 */
export default function TraceabilityTimeline({
  hitos = [],
  mode = 'operator',
  onEditStage,
  onDeleteStage,
  onAttachFile,
  onDetachFile,
}) {
  const sorted = [...hitos].sort(
    (a, b) => (a.orden ?? 0) - (b.orden ?? 0),
  );
  return (
    <ol className="relative space-y-3">
      {sorted.map((h, idx) => {
        const Icon = pickIcon(h.icono);
        const isLast = idx === sorted.length - 1;
        return (
          <li
            key={h._id || h.nombre + idx}
            className="relative pl-12 sm:pl-14"
          >
            {/* línea vertical */}
            {!isLast && (
              <span
                aria-hidden
                className="absolute left-4 sm:left-5 top-9 bottom-0 w-px -translate-y-2 bg-slate-200"
              />
            )}
            {/* icono del hito */}
            {/* El icono que eligio el operador, siempre. */}
            <span className="absolute left-0 top-0 flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 bg-primary/10 border-primary/30 text-primary">
              <Icon className="h-5 w-5" />
            </span>

            <div className="rounded-lg border bg-card p-3 sm:p-4 transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="min-w-0">
                  <h4 className="text-sm sm:text-base font-semibold text-foreground">
                    {h.nombre}
                  </h4>
                  {h.descripcion ? (
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">
                      {h.descripcion}
                    </p>
                  ) : null}
                  {h.fecha ? (
                    <p className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1">
                      <CalendarClock className="h-3 w-3" />
                      {formatDateTime(h.fecha)}
                    </p>
                  ) : null}
                </div>

                {/* Acciones (solo operador) */}
                {mode === 'operator' && (
                  <StageActions
                    h={h}
                    onEditStage={onEditStage}
                    onDeleteStage={onDeleteStage}
                    onAttachFile={onAttachFile}
                  />
                )}
              </div>

              {/* Archivos adjuntos */}
              {h.archivos && h.archivos.length > 0 && (
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {h.archivos.map((a) => (
                    <li
                      key={a.r2Key}
                      className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2 py-1.5 text-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate" title={a.nombreOriginal}>
                          {a.nombreOriginal}
                        </span>
                        {a.tamanoBytes ? (
                          <span className="text-muted-foreground text-[10px]">
                            {formatBytes(a.tamanoBytes)}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-1">
                        {a.urlPublica || a.r2Key ? (
                          <a
                            href={a.urlPublica || '#'}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline inline-flex items-center gap-1"
                            title="Descargar"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </a>
                        ) : null}
                        {mode === 'operator' && onDetachFile ? (
                          <button
                            type="button"
                            onClick={() => onDetachFile(h, a.r2Key)}
                            className="text-muted-foreground hover:text-destructive"
                            title="Quitar archivo"
                          >
                            ×
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function StageActions({ h, onEditStage, onDeleteStage, onAttachFile }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
      {onAttachFile ? (
        <button
          type="button"
          onClick={() => onAttachFile(h)}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
          title="Adjuntar archivo"
        >
          <Paperclip className="h-3.5 w-3.5" />
          Adjuntar
        </button>
      ) : null}
      {onEditStage ? (
        <button
          type="button"
          onClick={() => onEditStage(h)}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
          title="Editar hito"
        >
          <Pencil className="h-3.5 w-3.5" />
          Editar
        </button>
      ) : null}
      {onDeleteStage ? (
        <button
          type="button"
          onClick={() => onDeleteStage(h)}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50"
          title="Eliminar hito"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Eliminar
        </button>
      ) : null}
    </div>
  );
}
